import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
const _excluded = ["text", "tooltipVisible", "showOnFocus", "position", "ignoreTitleWarning", "html", "delay", "container"];
import Component from '../Component';
import classList from '../utils/classList';
import extractText from '../utils/extractText';
/**
 * The `Tooltip` component is used to create a tooltip for an element. It
 * requires a single child element to be passed to it. Passing multiple
 * children or fragments will throw an error.
 *
 * You should use this for any tooltips you create to allow for backwards
 * compatibility when we switch to another tooltip library instead of
 * Bootstrap tooltips.
 *
 * If you need to pass multiple children, surround them with another element,
 * such as a `<span>` or `<div>`.
 *
 * **Note:** this component will overwrite the `title` attribute of the first
 * child you pass to it, as this is how the current tooltip system works in
 * Flarum. This shouldn't be an issue if you're using this component correctly.
 *
 * @example <caption>Basic usage</caption>
 *          <Tooltip text="You wish!">
 *            <Button>
 *              Click for free money!
 *            </Button>
 *          </Tooltip>
 *
 * @example <caption>Use of `position` and `showOnFocus` attrs</caption>
 *          <Tooltip text="Woah! That's cool!" position="bottom" showOnFocus>
 *            <span>3 replies</span>
 *          </Tooltip>
 *
 * @example <caption>Incorrect usage</caption>
 *          // This is wrong! Surround the children with a <span> or similar.
 *          <Tooltip text="This won't work">
 *            Click
 *            <a href="/">here</a>
 *          </Tooltip>
 */
export default class Tooltip extends Component {
  constructor() {
    super(...arguments);
    _defineProperty(this, "firstChild", null);
    _defineProperty(this, "childDomNode", null);
    _defineProperty(this, "oldText", '');
    _defineProperty(this, "oldVisibility", void 0);
    _defineProperty(this, "shouldRecreateTooltip", false);
    _defineProperty(this, "shouldUpdateText", false);
    _defineProperty(this, "shouldChangeTooltipVisibility", false);
  }
  view(vnode) {
    /**
     * We know this will be a ChildArray and not a primitive as this
     * vnode is a component, not a text or trusted HTML vnode.
     */
    const children = vnode.children;

    // We remove these to get the remaining attrs to pass to the DOM element
    const _this$attrs = this.attrs,
      {
        text,
        tooltipVisible,
        showOnFocus = true,
        position = 'top',
        ignoreTitleWarning = false,
        html = false,
        delay = 0,
        container = false
      } = _this$attrs,
      attrs = _objectWithoutProperties(_this$attrs, _excluded);
    if (this.attrs.title && !ignoreTitleWarning) {
      console.warn('`title` attribute was passed to Tooltip component. Was this intentional? Tooltip content should be passed to the `text` attr instead.');
    }
    const realText = this.getRealText();

    // We need to update the tooltip text if it has changed
    if (realText !== this.oldText) {
      this.oldText = realText;
      this.shouldUpdateText = true;
    }
    if (tooltipVisible !== this.oldVisibility) {
      this.oldVisibility = this.attrs.tooltipVisible;
      this.shouldChangeTooltipVisibility = true;
    }

    // We'll try our best to detect any issues created by devs before they cause any weird effects.
    // Throwing an error will prevent the forum rendering, but will be better at alerting devs to
    // an issue.

    if (typeof children === 'undefined') {
      throw new Error("Tooltip component was provided with no direct child DOM element. Tooltips must contain a single direct DOM node to attach to.");
    }
    if (children.length !== 1) {
      throw new Error("Tooltip component was either passed more than one or no child node.\n\nPlease wrap multiple children in another element, such as a <div> or <span>.");
    }
    const firstChild = children[0];
    if (typeof firstChild !== 'object' || Array.isArray(firstChild) || firstChild === null) {
      throw new Error("Tooltip component was provided with no direct child DOM element. Tooltips must contain a single direct DOM node to attach to.");
    }
    if (typeof firstChild.tag === 'string' && ['#', '[', '<'].includes(firstChild.tag)) {
      throw new Error("Tooltip component with provided with a vnode with tag \"".concat(firstChild.tag, "\". This is not a DOM element, so is not a valid child element. Please wrap this vnode in another element, such as a <div> or <span>."));
    }
    this.firstChild = firstChild;
    return children;
  }
  oncreate(vnode) {
    super.oncreate(vnode);
    this.checkDomNodeChanged();
    this.recreateTooltip();
  }
  onupdate(vnode) {
    super.onupdate(vnode);
    this.checkDomNodeChanged();
    this.recreateTooltip();
    if (this.shouldUpdateText) {
      this.updateText();
      this.shouldUpdateText = false;
    }
  }
  onremove(vnode) {
    super.onremove(vnode);
    if (this.childDomNode !== null) {
      $(this.childDomNode).tooltip('destroy',
      // @ts-expect-error We don't want this arg to be part of the public API. It only exists to prevent deprecation warnings when using `$.tooltip` in this component.
      'DANGEROUS_tooltip_jquery_fn_deprecation_exempt');
    }
  }
  recreateTooltip() {
    if (this.shouldRecreateTooltip && this.childDomNode !== null) {
      const tooltip = $(this.childDomNode).data('bs.tooltip');
      if (tooltip && tooltip.$tip) {
        tooltip.$tip.removeClass('fade');
      }
      $(this.childDomNode).tooltip('destroy',
      // @ts-expect-error We don't want this arg to be part of the public API. It only exists to prevent deprecation warnings when using `$.tooltip` in this component.
      'DANGEROUS_tooltip_jquery_fn_deprecation_exempt');
      this.createTooltip();
      this.shouldRecreateTooltip = false;
    }
    if (this.shouldChangeTooltipVisibility) {
      this.shouldChangeTooltipVisibility = false;
      this.updateVisibility();
    }
  }
  updateText() {
    if (this.childDomNode === null) return;
    const realText = this.getRealText();
    this.childDomNode.setAttribute('data-original-title', realText);
    this.childDomNode.setAttribute('aria-label', realText);
    const tooltip = $(this.childDomNode).data('bs.tooltip');
    if (tooltip && tooltip.$tip && tooltip.$tip.hasClass('in')) {
      tooltip.$tip.find('.tooltip-inner')[this.attrs.html ? 'html' : 'text'](realText);
      tooltip.show();
    }
  }
  updateVisibility() {
    if (this.childDomNode === null) return;
    if (this.attrs.tooltipVisible === true) {
      $(this.childDomNode).tooltip('show',
      // @ts-expect-error We don't want this arg to be part of the public API. It only exists to prevent deprecation warnings when using `$.tooltip` in this component.
      'DANGEROUS_tooltip_jquery_fn_deprecation_exempt');
    } else if (this.attrs.tooltipVisible === false) {
      $(this.childDomNode).tooltip('hide',
      // @ts-expect-error We don't want this arg to be part of the public API. It only exists to prevent deprecation warnings when using `$.tooltip` in this component.
      'DANGEROUS_tooltip_jquery_fn_deprecation_exempt');
    }
  }
  createTooltip() {
    if (this.childDomNode === null) return;
    const {
      showOnFocus = true,
      position = 'top',
      delay,
      // This will have no effect when switching to CSS tooltips
      html = false,
      container = false,
      tooltipVisible,
      text
    } = this.attrs;

    // Fancy "hack" to assemble the trigger string
    const trigger = typeof tooltipVisible === 'boolean' ? 'manual' : classList('hover', [showOnFocus && 'focus']);
    const realText = this.getRealText();
    this.childDomNode.setAttribute('aria-label', realText);
    this.childDomNode.removeAttribute('title');

    // https://getbootstrap.com/docs/3.3/javascript/#tooltips-options
    $(this.childDomNode).tooltip({
      title: realText,
      html,
      delay,
      placement: position,
      trigger,
      container
    },
    // @ts-expect-error We don't want this arg to be part of the public API. It only exists to prevent deprecation warnings when using `$.tooltip` in this component.
    'DANGEROUS_tooltip_jquery_fn_deprecation_exempt');
  }
  getRealText() {
    const {
      text
    } = this.attrs;
    return Array.isArray(text) ? extractText(text) : text;
  }

  /**
   * Checks if the tooltip DOM node has changed.
   *
   * If it has, it updates `this.childDomNode` to the new node, and sets
   * `shouldRecreateTooltip` to `true`.
   */
  checkDomNodeChanged() {
    const domNode = this.firstChild.dom;
    if (domNode && !domNode.isSameNode(this.childDomNode)) {
      if (this.childDomNode) {
        const tooltip = $(this.childDomNode).data('bs.tooltip');
        if (tooltip && tooltip.$tip) {
          tooltip.$tip.removeClass('fade');
        }
        $(this.childDomNode).tooltip('destroy',
        // @ts-expect-error We don't want this arg to be part of the public API. It only exists to prevent deprecation warnings when using `$.tooltip` in this component.
        'DANGEROUS_tooltip_jquery_fn_deprecation_exempt');
      }
      this.childDomNode = domNode;
      this.shouldRecreateTooltip = true;
    }
  }
}
flarum.reg.add('core', 'common/components/Tooltip', Tooltip);